import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { RefObject } from 'react';

import { Bem } from '../../service/Bem/Bem';
import { TextPredictor } from '../../typings/predictor';
import { Loader } from '../Loader/Loader';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './PredictorAdvisor.css';
import * as keyset from './PredictorAdvisor.i18n';

export interface PredictorAdvisorProps {
    id?: string;
    isLoading?: boolean;
    text?: TextPredictor[];
    error?: string;
    isExpanderOpened: boolean;
    isGrowPerformanceEnabled?: boolean;

    onClose?: () => void;
    onOpen?: () => void;
}

export interface PredictorAdvisorState {
    isPopupVisible: boolean;
}

const i18n = i18nFactory(keyset);

const BLOCK = 'predictor-advisor';
const expanderBlock = 'predictor-expander-block';

export class PredictorAdvisor extends React.Component<
    PredictorAdvisorProps,
    PredictorAdvisorState
> {
    private anchor: React.ReactNode | null;
    private headerRef: RefObject<HTMLDivElement> = React.createRef();

    constructor(props: PredictorAdvisorProps) {
        super(props);

        this.state = {
            isPopupVisible: false
        };
    }

    render() {
        const {
            isLoading,
            isExpanderOpened,
            isGrowPerformanceEnabled = true
        } = this.props;

        return (
            <div className={BLOCK}>
                <div
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'buttons',
                        mods: {
                            coverage: isGrowPerformanceEnabled ? 'yes' : 'no'
                        }
                    })}
                >
                    {this.getCoverage()}
                    <div
                        ref={ref => (this.anchor = ref)}
                        onClick={this.changeStatusPopup}
                        className={Bem.cls(BLOCK, 'hint')}
                    >
                        ID
                    </div>
                </div>
                {isGrowPerformanceEnabled && isExpanderOpened && (
                    <div className={Bem.cls(expanderBlock, 'content')}>
                        {isLoading ? (
                            <Loader
                                cls={Bem.cls(expanderBlock, 'loader')}
                                size={'s'}
                            />
                        ) : (
                            this.getExpanderContent()
                        )}
                    </div>
                )}
                <PopupDecorator
                    cls={Bem.cls(BLOCK, 'popup')}
                    onClose={this.changeStatusPopup}
                    target={'anchor'}
                    anchor={this.anchor}
                    directions={['bottom-center']}
                    visible={this.state.isPopupVisible}
                    hasTail={true}
                    onOutsideClick={this.changeStatusPopup}
                >
                    {this.getId()}
                </PopupDecorator>
            </div>
        );
    }

    private getCoverage = () => {
        const {
            isExpanderOpened,
            isGrowPerformanceEnabled = true
        } = this.props;

        if (!isGrowPerformanceEnabled) {
            return null;
        }

        return (
            <div
                className={Bem.cls({
                    block: expanderBlock,
                    mods: {
                        opened: isExpanderOpened ? 'yes' : 'no'
                    }
                })}
            >
                {this.getHeader()}
            </div>
        );
    };

    private getHeader = () => {
        return (
            <div
                className={Bem.cls(expanderBlock, 'header')}
                onClick={this.toggleView}
                ref={this.headerRef}
            >
                <div className={Bem.cls(expanderBlock, 'title')}>
                    {i18n('increase')}
                </div>
                {this.getExpanderControl()}
            </div>
        );
    };

    private changeStatusExpander = () => {
        this.props.isExpanderOpened ? this.close() : this.open();
    };

    private close = () => {
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    private open = () => {
        if (this.props.onOpen) {
            this.props.onOpen();
        }
    };

    private changeStatusPopup = () => {
        this.setState({
            isPopupVisible: !this.state.isPopupVisible
        });
    };

    private getId = () => {
        const { id } = this.props;

        if (!id) {
            return null;
        }

        return <div>{i18n('id-predictor', { id: id })}</div>;
    };

    private toggleView = (event: React.MouseEvent<HTMLDivElement>) => {
        let target = event.target as HTMLElement;

        while (target !== this.headerRef.current) {
            if (target.tagName === 'A') {
                return;
            }

            if (target.parentElement !== null) {
                target = target.parentElement;
            }
        }

        this.changeStatusExpander();
    };

    private getExpanderControl = () => {
        const { isExpanderOpened } = this.props;

        return (
            <Shape2
                cls={Bem.cls({
                    block: expanderBlock,
                    elem: 'control-element',
                    mods: {
                        opened: isExpanderOpened ? 'yes' : 'no'
                    }
                })}
                hoverable={true}
                theme={Shape2Theme.light}
                icon={Shape2Icon.verticalArrow}
                width={14}
                height={14}
                onClick={this.changeStatusExpander}
            />
        );
    };

    private getExpanderContent = () => {
        const { text, error } = this.props;

        if (error) {
            return i18n('expander-error');
        }

        if (text && text.length) {
            return (
                <div>
                    <div className={Bem.cls(expanderBlock, 'content-header')}>
                        {i18n('expander-header')}
                    </div>
                    <div className={Bem.cls(expanderBlock, 'content-body')}>
                        {text.map((item, index) => {
                            return (
                                <div
                                    key={index}
                                    className={Bem.cls(
                                        expanderBlock,
                                        'content-item'
                                    )}
                                >
                                    <div>{item.title}</div>
                                    <div>+{item.value}%</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else {
            return i18n('expander-no-data');
        }
    };
}
